import { useState, useEffect, useContext } from "react";
import { Dialog, Grid, MenuItem, Box } from "@mui/material";
import toast from "react-hot-toast";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import CustomTextField from "components/utils/CustomFields/CustomTextField";
import ShopContext from "context/ShopContext";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { PrinterManager } from "managers/PrinterManager";
import * as TerminalHelper from "helpers/TermialHelper";
import { textToBytes, bytesToBase64 } from "helpers/StringHelper";
import { PRINTER_TYPES } from "const";

const FIELDS = [
  ["quantity", "Quantity"],
  ["shelfDays", "Shelf Days", "number"],
  ["packedDate", "Packed Date", "date"],
  ["expiryDate", "Expiry Date", "date"],
  ["batch", "Batch"],
  ["note1", "Custom Note 1"],
  ["note2", "Custom Note 2"],
  ["copies", "Number of copies", "number"],
];

const getFormattedPluQuantity = (plu, qtyKg) => {
  if (plu == null || qtyKg == null) return "";

  const pluPart = String(plu).padStart(4, "0");
  const qtyGrams = Math.floor(qtyKg * 1000);
  const qtyPart = String(qtyGrams).padStart(5, "0");

  return `$${pluPart}${qtyPart}$`;
};

const addDaysToDate = (dateStr, days) => {
  if (!dateStr || isNaN(days)) return "";
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + Number(days));
  const resY = date.getFullYear();
  const resM = String(date.getMonth() + 1).padStart(2, "0");
  const resD = String(date.getDate()).padStart(2, "0");
  return `${resY}-${resM}-${resD}`;
};

const calculateDiffDays = (packedStr, expiryStr) => {
  if (!packedStr || !expiryStr) return "";
  const [py, pm, pd] = packedStr.split("-").map(Number);
  const [ey, em, ed] = expiryStr.split("-").map(Number);
  const pDate = new Date(py, pm - 1, pd);
  const eDate = new Date(ey, em - 1, ed);
  const diffDays = Math.round((eDate.getTime() - pDate.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : "";
};

export default function PrintBarcodeDialog({ open, onClose, item, onPrint }) {
  const { selectedShop } = useContext(ShopContext);
  const templates = selectedShop?.config?.barcode_settings?.barcode_custom_prn_templates || [];

  const [template, setTemplate] = useState("");
  const [form, setForm] = useState({
    packedDate: new Date().toISOString().slice(0, 10),
    copies: 1,
  });

  useEffect(() => {
    if (open) {
      setForm({
        packedDate: new Date().toISOString().slice(0, 10),
        copies: 1,
      });
    }
  }, [open]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };

      if (key === "shelfDays") {
        const days = parseInt(value, 10);
        if (!isNaN(days) && updated.packedDate) {
          updated.expiryDate = addDaysToDate(updated.packedDate, days);
        } else if (!value) {
          updated.expiryDate = "";
        }
      } else if (key === "packedDate") {
        if (prev.shelfDays) {
          updated.expiryDate = addDaysToDate(value, parseInt(prev.shelfDays, 10));
        } else if (prev.expiryDate) {
          updated.shelfDays = calculateDiffDays(value, prev.expiryDate);
        }
      } else if (key === "expiryDate") {
        if (updated.packedDate && value) {
          updated.shelfDays = calculateDiffDays(updated.packedDate, value);
        }
      }

      return updated;
    });
  };

  if (!open) return null;

  const generatePrnData = (setting) => {
    if (!setting?.code) return "";
    const items = item ? (Array.isArray(item) ? item : [item]) : [];
    const count = parseInt(form.copies) || 1;
    const barcodeCustomNote = TerminalHelper.getTerminalSettings()?.barcodeCustomNote || "";
    let output = "";

    items.forEach((itm) => {
      const qty = parseInt(itm.quantity ?? count) || 1;
      const rows = setting.column ? (qty % setting.column === 0 ? qty : qty + 1) : qty;
      const isKg = String(itm.unit || "").toLowerCase() === "kg";
      const divisor = isKg ? 1000 : Number(form.quantity) || 1;
      const unitLabel = isKg ? "g" : itm.unit || "N";
      const uspPrice =
        itm.price != null ? `${(Number(itm.price || 0) / divisor).toFixed(2)}${unitLabel ? ` /${unitLabel}` : ""}` : "";
      const uspMrp =
        itm.mrp != null ? `${(Number(itm.mrp || 0) / divisor).toFixed(2)}${unitLabel ? ` /${unitLabel}` : ""}` : "";
      const currentQty = Number(form.quantity) || qty;
      const total = (currentQty * Number(itm.price || 0)).toFixed(2);
      const totalMrp = (currentQty * Number(itm.mrp || 0)).toFixed(2);
      const qtyQr = itm.plu != null && currentQty != null ? getFormattedPluQuantity(itm.plu, Number(currentQty)) : "";

      for (let i = 0; i < rows; i++) {
        let code = setting.code
          .replace(/<title>/g, selectedShop?.shop_name || "")
          .replace(/<name>/g, itm.name || "")
          .replace(/<barcode>/g, itm.ean && itm.ean !== "nil" ? itm.ean : itm.plu ? `plu${itm.plu}` : "")
          .replace(/<qty-qr>/g, qtyQr)
          .replace(/<price>/g, itm.price ? String(itm.price) : "")
          .replace(/<mrp>/g, itm.mrp ? String(itm.mrp) : "")
          .replace(/<usp>/g, uspPrice)
          .replace(/<usp-mrp>/g, uspMrp)
          .replace(/<plu>/g, itm.plu ? String(itm.plu) : "")
          .replace(/<quantity>/g, String(form.quantity || qty))
          .replace(/<total>/g, total ? String(total) : "")
          .replace(/<total-mrp>/g, totalMrp ? String(totalMrp) : "")
          .replace(/<batch>/g, form.batch || "")
          .replace(/<packed>/g, form.packedDate ? DateTimeHelpers.convertDateToIsoDMY(new Date(form.packedDate)) : "")
          .replace(/<expiry>/g, form.expiryDate ? DateTimeHelpers.convertDateToIsoDMY(new Date(form.expiryDate)) : "")
          .replace(/<custom-note>/g, barcodeCustomNote || itm.note || "")
          .replace(/<custom-note-1>/g, form.note1 || "")
          .replace(/<custom-note-2>/g, form.note2 || "");

        output += code + "\n";
      }
    });

    return output;
  };

  const handlePrint = async () => {
    const setting = templates.find((t) => (t.id || t.name) === template || t.name === template);
    if (!setting) {
      return toast.error("Please select a template");
    }

    const prnData = generatePrnData(setting);

    if (typeof onPrint === "function") {
      onPrint({ ...form, template, item, prnData });
    } else {
      try {
        const terminalSettings = TerminalHelper.getTerminalSettings();
        const printerType = terminalSettings.barcodePrinterType || PRINTER_TYPES.USB;
        const printerTarget =
          printerType === PRINTER_TYPES.LAN
            ? terminalSettings.invoice_printer_ip
            : terminalSettings.barcodePrinter || terminalSettings.printer;

        await PrinterManager.print({
          content: bytesToBase64(textToBytes(prnData)),
          printerType,
          printerTarget,
          label: "BARCODE PRINT",
        });
        toast.success(`Printing ${form.copies || 1} barcode label(s)...`);
      } catch (err) {
        toast.error("Failed to print barcode");
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { p: 2.5, borderRadius: "12px", width: 440 } }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <CustomTextField
            select
            label="Custom PRN Template"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            {templates.map((t) => (
              <MenuItem key={t.id || t.name} value={t.name}>
                {t.name}
              </MenuItem>
            ))}
          </CustomTextField>
        </Grid>

        {FIELDS.map(([key, label, type]) => (
          <Grid item xs={6} key={key}>
            <CustomTextField
              label={label}
              type={type || "text"}
              value={form[key] || ""}
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={1.5} pt={1}>
            <ButtonGray onClick={onClose}>Cancel</ButtonGray>
            <ButtonRed onClick={handlePrint}>Print</ButtonRed>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
}
